import React, { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Warning, ArrowClockwise } from "@phosphor-icons/react";

const BUCKETS = [
  { key: "current", label: "Not yet due", color: "text-slate-700",  bar: "bg-slate-500" },
  { key: "1_30",    label: "1–30 days",   color: "text-amber-700",  bar: "bg-amber-500" },
  { key: "31_60",   label: "31–60 days",  color: "text-orange-700", bar: "bg-orange-500" },
  { key: "61_90",   label: "61–90 days",  color: "text-red-700",    bar: "bg-red-500" },
  { key: "over_90", label: "Over 90",     color: "text-red-900",    bar: "bg-red-800" },
];

export default function Aging() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/reports/aging").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="p-8 text-slate-500">Loading…</div>;

  const grand = d.grand_total || 0;

  return (
    <div data-testid="aging-page">
      <PageHeader
        title="Aging Report"
        subtitle="Outstanding invoices bucketed by days overdue."
        actions={
          <button onClick={load} className="text-sm text-slate-600 hover:text-[#EA580C] flex items-center gap-1" data-testid="refresh-aging">
            <ArrowClockwise size={14} /> Refresh
          </button>
        }
      />

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {BUCKETS.map((b) => {
            const total = d.totals[b.key];
            const pct = grand > 0 ? (total / grand) * 100 : 0;
            return (
              <div key={b.key} className="bg-white border border-slate-200 p-4 rounded-sm" data-testid={`aging-bucket-${b.key}`}>
                <div className="label-caps">{b.label}</div>
                <div className={`font-display text-xl font-bold font-mono-num mt-2 ${b.color}`}>{fmtMoney(total)}</div>
                <div className="mt-3 h-1 bg-slate-100 relative">
                  <div className={`absolute inset-y-0 left-0 ${b.bar}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="text-xs text-slate-500 mt-1">{d.buckets[b.key].length} invoices</div>
              </div>
            );
          })}
        </div>

        <div className="bg-[#0F172A] text-white p-5 rounded-sm flex items-center justify-between">
          <div>
            <div className="label-caps text-slate-400">Grand total outstanding</div>
            <div className="font-display text-3xl font-bold font-mono-num mt-2 text-[#EA580C]" data-testid="aging-grand-total">{fmtMoney(grand)}</div>
          </div>
          {(d.totals.over_90 > 0 || d.totals["61_90"] > 0) && (
            <div className="flex items-center gap-2 text-amber-300 text-sm">
              <Warning size={20} weight="fill" />
              <span>Action required on severely overdue invoices</span>
            </div>
          )}
        </div>

        {BUCKETS.map((b) => {
          const rows = d.buckets[b.key];
          if (rows.length === 0) return null;
          return (
            <div key={b.key} className="bg-white border border-slate-200 rounded-sm">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className={`label-caps ${b.color}`}>{b.label}</div>
                <div className="text-sm font-mono-num font-semibold">{fmtMoney(d.totals[b.key])}</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50"><tr>
                  <th className="p-3 text-left label-caps">Invoice</th>
                  <th className="p-3 text-left label-caps">Client</th>
                  <th className="p-3 text-left label-caps">Due Date</th>
                  <th className="p-3 text-right label-caps">Days Overdue</th>
                  <th className="p-3 text-right label-caps">Balance</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="p-3 font-mono-num font-medium">{r.invoice_number}</td>
                      <td className="p-3">{r.client_name}</td>
                      <td className="p-3 text-slate-600">{fmtDate(r.due_date)}</td>
                      <td className="p-3 text-right font-mono-num">{r.days_overdue}</td>
                      <td className={`p-3 text-right font-mono-num font-semibold ${b.color}`}>{fmtMoney(r.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}

        {grand === 0 && <EmptyState title="All clear" description="No outstanding invoices right now. Nicely done." />}
      </div>
    </div>
  );
}
